import React, { useContext, useEffect } from "react";
import { Redirect, Route, useLocation } from "react-router-dom";
import { AuthContext } from "../context/authContext";
import { FullPageLoader } from "./Loader";
import { useAlert } from "react-alert";
import { roless } from "../Constants";
import { FaWindows } from "react-icons/fa";
export interface Dt extends React.ComponentProps<any> {
  isPrivate: boolean;
  roles?: string[];
}

export const SpecialRoute = ({ isPrivate, roles, ...props }: Dt) => {
  const { loading, ready, user, role } = useContext(AuthContext);
  const location = useLocation();
  const alert = useAlert();

  // Show loader while authenticating
  if (loading || !ready) {
    console.log("SpecialRoute - Loading or not ready, showing loader");
    return <FullPageLoader isLoading={true} message="Loading..." />;
  }

  // Handle the private route when user is not authenticated
  if (!user && isPrivate) {
    console.log("Redirecting to login: Not authenticated for private route");
    return (
      <Redirect
        to={{
          pathname: "/",
          state: { from: location },
        }}
      />
    );
  }

  // Special handling for admin roles
  const isAdmin = role === "IT Admin" || role === "Business Admin";

  // If user is authenticated but trying to access a role-restricted page
  // Admins can access all routes
  if (user && isPrivate && roles && role && !isAdmin) {
    // Check if the role matches any of the allowed roles (using exact matching or includes)
    const roleMatches = roles.some(allowedRole => {
      return role === allowedRole || role.includes(allowedRole) || allowedRole.includes(role);
    });
    
    if (!roleMatches) {
      // Check if we're already on a valid dashboard to prevent redirect loops
      const currentPath = location.pathname;
      const targetPath = role === "Warehouse Manager" ? "/warehousemanagerdashboard" : "/dashboard";
      
      // Only redirect if we're not already on the correct dashboard
      if (currentPath !== targetPath) {
        console.log("Access denied: Role not in allowed roles");
        alert.error("You don't have permission to access this page");
        return (
          <Redirect
            to={{
              pathname: targetPath,
              state: { from: location },
            }}
          />
        );
      }
    }
  }

  if (user && !isPrivate) {
    // Check current path to prevent redirect loops
    const currentPath = location.pathname;
    const targetPath = role === "Warehouse Manager" ? "/warehousemanagerdashboard" : "/dashboard";
    
    // Only redirect if we're not already on the correct dashboard
    if (currentPath !== targetPath) {
      console.log("Redirecting to dashboard: Authenticated user accessing public route");
      return (
        <Redirect
          to={{
            pathname: targetPath,
            state: { from: location },
          }}
        />
      );
    }
  }

  // Pass through to the requested route
  console.log("Rendering requested route:", props.path);
  return <Route {...props} />;
};

const AuthedRoute =
  (isPrivate: boolean) => (props: React.ComponentProps<any>) =>
    <SpecialRoute {...{ isPrivate, ...props }} />;

export const InternalRoute = AuthedRoute(true);
export const ExternalRoute = AuthedRoute(false);